import { supabase } from './supabaseClient';
import { kpiAdminService } from './kpiAdminService';

export const reportAdminService = {
  // ---- Report systems (mirror piAdminService.getPISystems et al) ----
  async getReportSystems() {
    const { data, error } = await supabase
      .from('report_systems')
      .select('*')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async createReportSystem(systemData) {
    const { data, error } = await supabase
      .from('report_systems')
      .insert([systemData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updateReportSystem(reportSystemId, updates) {
    const { data, error } = await supabase
      .from('report_systems')
      .update(updates)
      .eq('id', reportSystemId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deactivateReportSystem(reportSystemId) {
    return this.updateReportSystem(reportSystemId, { is_active: false });
  },

  // ---- System-scoped fields (free-form, like kpi_definitions) ----
  // Active fields of one system, ordered by sort_order. report_system_id is the
  // scope; position is gone.
  async getReportSystemFields(reportSystemId) {
    const { data, error } = await supabase
      .from('report_fields')
      .select('*')
      .eq('report_system_id', reportSystemId)
      .eq('is_active', true)
      .order('sort_order', { ascending: true });
    if (error) throw error;
    return data;
  },

  // fieldData must carry report_system_id; callers no longer pass position.
  async createReportField(fieldData) {
    const { data, error } = await supabase
      .from('report_fields')
      .insert([fieldData])
      .select();
    if (error) throw error;
    return data[0];
  },

  async updateReportField(reportFieldId, updates) {
    const { data, error } = await supabase
      .from('report_fields')
      .update(updates)
      .eq('id', reportFieldId)
      .select();
    if (error) throw error;
    return data[0];
  },

  async deactivateReportField(reportFieldId) {
    return this.updateReportField(reportFieldId, { is_active: false });
  },

  // ---- User report-system assignments (mirror piAdminService) ----
  async getUserReportAssignments() {
    const { data, error } = await supabase
      .from('user_report_system_assignments')
      .select('*, user_id, report_system_id')
      .order('created_at', { ascending: false });
    if (error) throw error;
    return data;
  },

  async assignReportSystemToUser(userId, reportSystemId) {
    const { data, error } = await supabase
      .from('user_report_system_assignments')
      .insert([{
        user_id: userId,
        report_system_id: reportSystemId,
        is_active: true,
      }])
      .select();
    if (error) throw error;
    return data[0];
  },

  async removeReportSystemFromUser(userId, reportSystemId) {
    const { error } = await supabase
      .from('user_report_system_assignments')
      .delete()
      .eq('user_id', userId)
      .eq('report_system_id', reportSystemId);
    if (error) throw error;
  },

  // Reuse the existing users query so report assignment UI has one source of truth.
  async getAllUsers() {
    return kpiAdminService.getAllUsers();
  },

  // Admin-only RPC: bumps due_since to now() on all the user's report
  // assignments so past-due stats restart from this moment. Returns the
  // number of assignments updated.
  async resetReportPastDue(userId) {
    const { data, error } = await supabase.rpc('reset_report_past_due', {
      p_user_id: userId,
    });
    if (error) throw error;
    return data;
  },
};
